/**
 * Снимката от телефона е 4–8 MB. По румънска магистрала с два „чертички"
 * обхват такъв файл не тръгва. Смаляваме на устройството ПРЕДИ да влезе в
 * опашката — качва се само това, което ще тръгне.
 *
 * Числата (ADR 0009): дълга страна 1600 px и JPEG качество 0.80 при нормален
 * режим — ЧМР от 4 MB става около 300 KB и текстът остава четим при разчитане.
 * „Пестелив" режим: 1200 px и 0.65 — около 150 KB, за скъп роуминг.
 */

export type PhotoQuality = 'normal' | 'economy';

export interface PhotoProfile {
  maxEdge: number;
  quality: number;
  /** Груба очаквана големина на файл, в килобайти — показва се в „Настройки" */
  approxKb: number;
}

export const PHOTO_PROFILES: Record<PhotoQuality, PhotoProfile> = {
  normal: { maxEdge: 1600, quality: 0.8, approxKb: 300 },
  economy: { maxEdge: 1200, quality: 0.65, approxKb: 150 },
};

export interface ProcessedImage {
  blob: Blob;
  width: number;
  height: number;
  thumbDataUrl: string;
}

interface Drawable {
  width: number;
  height: number;
  source: CanvasImageSource;
  release: () => void;
}

async function load(file: Blob): Promise<Drawable> {
  if (typeof createImageBitmap === 'function') {
    try {
      // `from-image` спазва EXIF ориентацията — иначе ЧМР-то излиза легнало.
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return {
        width: bitmap.width,
        height: bitmap.height,
        source: bitmap,
        release: () => bitmap.close(),
      };
    } catch {
      // стари браузъри без втория аргумент — минаваме през <img>
    }
  }
  const url = URL.createObjectURL(file);
  const element = new Image();
  element.decoding = 'async';
  await new Promise<void>((resolve, reject) => {
    element.onload = () => resolve();
    element.onerror = () => reject(new Error('Снимката не може да се отвори'));
    element.src = url;
  });
  return {
    width: element.naturalWidth,
    height: element.naturalHeight,
    source: element,
    release: () => URL.revokeObjectURL(url),
  };
}

function draw(image: Drawable, targetWidth: number, targetHeight: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Браузърът не може да обработи снимката');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, targetWidth, targetHeight);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image.source, 0, 0, targetWidth, targetHeight);
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Снимката не можа да се запише'))),
      'image/jpeg',
      quality,
    );
  });
}

export async function processPhoto(file: Blob, quality: PhotoQuality): Promise<ProcessedImage> {
  const profile = PHOTO_PROFILES[quality];
  const image = await load(file);
  try {
    const scale = Math.min(1, profile.maxEdge / Math.max(image.width, image.height));
    const width = Math.max(1, Math.round(image.width * scale));
    const height = Math.max(1, Math.round(image.height * scale));

    const blob = await toBlob(draw(image, width, height), profile.quality);

    const thumbScale = Math.min(1, 220 / Math.max(width, height));
    const thumbCanvas = draw(
      image,
      Math.max(1, Math.round(width * thumbScale)),
      Math.max(1, Math.round(height * thumbScale)),
    );

    return {
      blob,
      width,
      height,
      thumbDataUrl: thumbCanvas.toDataURL('image/jpeg', 0.6),
    };
  } finally {
    image.release();
  }
}

/** Подписът е рисунка, не снимка: PNG, защото линиите трябва да останат остри. */
export function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Подписът не можа да се запише'))),
      'image/png',
    );
  });
}
